export function mat4Identity(): number[] {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

export function mat4Multiply(a: number[], b: number[]): number[] {
  const out = new Array(16);
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      out[r * 4 + c] =
        a[r * 4 + 0] * b[0 * 4 + c] +
        a[r * 4 + 1] * b[1 * 4 + c] +
        a[r * 4 + 2] * b[2 * 4 + c] +
        a[r * 4 + 3] * b[3 * 4 + c];
    }
  }
  return out;
}

export function mat4Perspective(fovyRad: number, aspect: number, near: number, far: number): number[] {
  const f = 1 / Math.tan(fovyRad / 2);
  const nf = 1 / (near - far);
  return [
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (far + near) * nf, -1,
    0, 0, 2 * far * near * nf, 0,
  ];
}

export function mat4Translate(m: number[], x: number, y: number, z: number): number[] {
  const t = mat4Identity();
  t[12] = x; t[13] = y; t[14] = z;
  return mat4Multiply(m, t);
}

export function mat4RotateY(m: number[], a: number): number[] {
  const c = Math.cos(a), s = Math.sin(a);
  const r = [c, 0, s, 0, 0, 1, 0, 0, -s, 0, c, 0, 0, 0, 0, 1];
  return mat4Multiply(m, r);
}

export function mat4RotateX(m: number[], a: number): number[] {
  const c = Math.cos(a), s = Math.sin(a);
  const r = [1, 0, 0, 0, 0, c, -s, 0, 0, s, c, 0, 0, 0, 0, 1];
  return mat4Multiply(m, r);
}

function vec3Normalize(v: number[]): number[] {
  const len = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / len, v[1] / len, v[2] / len];
}

function vec3Cross(a: number[], b: number[]): number[] {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function vec3Dot(a: number[], b: number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function mat4LookAt(eye: number[], center: number[], up: number[]): number[] {
  const f = vec3Normalize([center[0] - eye[0], center[1] - eye[1], center[2] - eye[2]]);
  const s = vec3Normalize(vec3Cross(f, up));
  const u = vec3Cross(s, f);
  return [
    s[0], u[0], -f[0], 0,
    s[1], u[1], -f[1], 0,
    s[2], u[2], -f[2], 0,
    -vec3Dot(s, eye), -vec3Dot(u, eye), vec3Dot(f, eye), 1,
  ];
}

export function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(sh) || 'Shader compile failed';
    gl.deleteShader(sh);
    throw new Error(info);
  }
  return sh;
}

export function createProgram(gl: WebGLRenderingContext, vsSrc: string, fsSrc: string): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  const prog = gl.createProgram()!;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(prog) || 'Program link failed';
    gl.deleteProgram(prog);
    throw new Error(info);
  }
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return prog;
}

export function buildCubeGeometry(): { verts: Float32Array; indices: Uint16Array } {
  const p = [
    -1, -1, 1, 0, 0, 1, 0, 0,
    1, -1, 1, 0, 0, 1, 1, 0,
    1, 1, 1, 0, 0, 1, 1, 1,
    -1, 1, 1, 0, 0, 1, 0, 1,
    -1, -1, -1, 0, 0, -1, 0, 0,
    -1, 1, -1, 0, 0, -1, 0, 1,
    1, 1, -1, 0, 0, -1, 1, 1,
    1, -1, -1, 0, 0, -1, 1, 0,
    -1, 1, -1, 0, 1, 0, 0, 0,
    -1, 1, 1, 0, 1, 0, 0, 1,
    1, 1, 1, 0, 1, 0, 1, 1,
    1, 1, -1, 0, 1, 0, 1, 0,
    -1, -1, -1, 0, -1, 0, 0, 0,
    1, -1, -1, 0, -1, 0, 1, 0,
    1, -1, 1, 0, -1, 0, 1, 1,
    -1, -1, 1, 0, -1, 0, 0, 1,
    1, -1, -1, 1, 0, 0, 0, 0,
    1, 1, -1, 1, 0, 0, 0, 1,
    1, 1, 1, 1, 0, 0, 1, 1,
    1, -1, 1, 1, 0, 0, 1, 0,
    -1, -1, -1, -1, 0, 0, 0, 0,
    -1, -1, 1, -1, 0, 0, 1, 0,
    -1, 1, 1, -1, 0, 0, 1, 1,
    -1, 1, -1, -1, 0, 0, 0, 1,
  ];
  const idx = [
    0, 1, 2, 0, 2, 3,
    4, 5, 6, 4, 6, 7,
    8, 9, 10, 8, 10, 11,
    12, 13, 14, 12, 14, 15,
    16, 17, 18, 16, 18, 19,
    20, 21, 22, 20, 22, 23,
  ];
  return { verts: new Float32Array(p), indices: new Uint16Array(idx) };
}

export function createCheckerTexture(gl: WebGLRenderingContext): WebGLTexture {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext('2d')!;
  for (let y = 0; y < 8; y++) {
    for (let x = 0; x < 8; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? 'rgba(100,181,255,1)' : 'rgba(31,191,117,1)';
      ctx.fillRect(x * 8, y * 8, 8, 8);
    }
  }
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
  gl.generateMipmap(gl.TEXTURE_2D);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}
